import { AUTO_LAUNCH_RULES, CORE_PHYSICS, GAME_FRAME } from "../../config/gameplay";
import { createCoreFromAutoLaunch } from "../cores/factory";
import type { OrbitForgeSave } from "../save/types";
import type { CoreState } from "../runtime/types";
import type { UpgradeEffects } from "../upgrades/effects";
import type { FeedbackDraft } from "../runtime/feedback";
import { autoLaunchBallsPerSecond } from "./effects";
import { consumeBestAutoExtendTicket } from "./commands";

export interface AutoLaunchAdvanceInput {
  readonly save: OrbitForgeSave;
  readonly cores: readonly CoreState[];
  readonly effects: UpgradeEffects;
  readonly deltaSeconds: number;
  readonly stormActive: boolean;
}

export interface AutoLaunchAdvanceResult {
  readonly cores: readonly CoreState[];
  readonly dirty: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

export function advanceAutoLaunch(input: AutoLaunchAdvanceInput): AutoLaunchAdvanceResult {
  const state = input.save.autoLaunch;
  const feedback: FeedbackDraft[] = [];
  let cores = input.cores;
  let dirty = false;

  if (state.activeRemainingSeconds <= 0) {
    if (state.autoExtendEnabled && consumeBestAutoExtendTicket(input.save)) {
      dirty = true;
      feedback.push({ kind: "pulse", target: "auto" });
    } else {
      dirty = state.activeTicketSeconds !== null || state.spawnBudget > 0;
      state.activeTicketSeconds = null;
      state.spawnBudget = 0;
      return { cores, dirty, feedback };
    }
  }

  if (input.stormActive) {
    return { cores, dirty, feedback };
  }

  state.activeRemainingSeconds = Math.max(0, state.activeRemainingSeconds - input.deltaSeconds);
  dirty = true;
  state.spawnBudget = Math.min(AUTO_LAUNCH_RULES.spawnBudgetCap, state.spawnBudget + autoLaunchBallsPerSecond(input.save) * input.deltaSeconds);

  const activeProgressCores = cores.filter((core) => core.countsAgainstLaunchLimit).length;
  if (activeProgressCores >= input.effects.maxCores || cores.length >= CORE_PHYSICS.hardActiveCoreCap) {
    state.spawnBudget = Math.min(state.spawnBudget, AUTO_LAUNCH_RULES.fullLimitBudgetClamp);
    return expireIfNeeded(input.save, cores, dirty, feedback);
  }

  const spawnCount = Math.min(
    AUTO_LAUNCH_RULES.maxSpawnsPerFrame,
    Math.floor(state.spawnBudget),
    input.effects.maxCores - activeProgressCores
  );

  for (let index = 0; index < spawnCount; index += 1) {
    state.spawnBudget -= 1;
    const activeOverchargedCores = cores.filter((core) => core.isOvercharged && core.countsAsProgress).length;
    const result = createCoreFromAutoLaunch(input.save, input.effects, { activeOverchargedCores });
    input.save.rngState = result.nextRngState;
    input.save.nextEntityId += 1;
    input.save.stats.TotalCoresLaunched += 1;
    input.save.stats.DailyCoresLaunched += 1;
    input.save.stats.WeeklyCoresLaunched += 1;
    if (result.core.isOvercharged) {
      input.save.stats.OverchargedCoresLaunched += 1;
    }
    cores = [...cores, result.core];
    feedback.push({ kind: "core-spawn", frameX: result.core.position.x + GAME_FRAME.width / 2, frameY: result.core.position.y + GAME_FRAME.height / 2, color: result.core.color });
    if (result.core.isOvercharged) {
      feedback.push({ kind: "rare-core", frameX: result.core.position.x + GAME_FRAME.width / 2, frameY: result.core.position.y + GAME_FRAME.height / 2 - 34, color: "gold", text: `RARE x${result.core.rewardMultiplier}` });
    }
  }

  return expireIfNeeded(input.save, cores, dirty, feedback);
}

function expireIfNeeded(save: OrbitForgeSave, cores: readonly CoreState[], dirty: boolean, feedback: FeedbackDraft[]): AutoLaunchAdvanceResult {
  if (save.autoLaunch.activeRemainingSeconds <= 0) {
    if (save.autoLaunch.autoExtendEnabled && consumeBestAutoExtendTicket(save)) {
      dirty = true;
      feedback.push({ kind: "pulse", target: "auto" });
    } else {
      save.autoLaunch.activeTicketSeconds = null;
      save.autoLaunch.spawnBudget = 0;
      feedback.push({ kind: "floating-text", target: "auto", frameX: 180, frameY: 372, color: "white", text: "AUTO END" });
    }
  }
  return { cores, dirty, feedback };
}
